interface DocxDownloadResult {
  slices: Uint8Array[];

  /**
   * The filename and URL of the downloaded file.
   * These aren't available in certain contexts, like WOPI.
   */
  properties?: {
    filename: string;
    url: string;
  };
}

export async function downloadDocx(): Promise<DownloadedDocument> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(Office.FileType.Compressed, async (asyncOfficeResult) => {
      try {
        const docxDownloadResult = await handleFileResult(asyncOfficeResult);
        const downloadedDocument = new DownloadedDocument(docxDownloadResult.slices, docxDownloadResult.properties);

        resolve(downloadedDocument);
      } catch (e) {
        reject(e);
      }
    });
  });
}

export class DownloadedDocument {
  constructor(public readonly slices: Uint8Array[], public readonly properties?: { filename: string; url: string }) {}

  get base64(): string {
    // Combine all slices into a single Uint8Array
    const totalLength = this.slices.reduce((sum, slice) => sum + slice.length, 0);
    const combined = new Uint8Array(totalLength);
    let offset = 0;
    for (const slice of this.slices) {
      combined.set(slice, offset);
      offset += slice.length;
    }

    // Convert to base64 using chunks to avoid call stack issues with large arrays
    const CHUNK_SIZE = 0x8000; // 32KB chunks
    let binaryString = "";
    for (let i = 0; i < combined.length; i += CHUNK_SIZE) {
      const chunk = combined.subarray(i, Math.min(i + CHUNK_SIZE, combined.length));
      binaryString += String.fromCharCode.apply(null, chunk as unknown as number[]);
    }

    return btoa(binaryString);
  }

  get blob(): Blob {
    return new Blob(this.slices as BlobPart[], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
  }
}

async function handleFileResult(asyncOfficeResult: Office.AsyncResult<Office.File>): Promise<DocxDownloadResult> {
  if (asyncOfficeResult.status !== Office.AsyncResultStatus.Succeeded) {
    asyncOfficeResult.value.closeAsync();
    throw asyncOfficeResult;
  }

  try {
    const slices = await getFileSlicesData(asyncOfficeResult.value);
    const getFileProperties = (): Promise<DocxDownloadResult> => {
      return new Promise((resolve, reject) => {
        try {
          Office.context.document.getFilePropertiesAsync((properties) => {
            if (properties.status === Office.AsyncResultStatus.Succeeded) {
              const url = properties.value.url;
              const fileParts = url.split(/[\\/]+/);
              const filename = fileParts[fileParts.length - 1];
              resolve({ slices, properties: { filename, url } });
            } else {
              resolve({ slices });
            }
          });
        } catch (e: unknown) {
          reject(e);
        }
      });
    };

    return getFileProperties();
  } finally {
    asyncOfficeResult.value.closeAsync();
  }
}

async function getFileSlicesData(file: Office.File): Promise<Uint8Array[]> {
  // NOTE: This is only the right type if you use Office.FileType.Compressed
  let slices: Uint8Array[] = [];

  for (let index = 0; index < file.sliceCount; index++) {
    await new Promise<void>((resolve, reject) => {
      file.getSliceAsync(index, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          file.closeAsync();
          reject(`${asyncResult.error.code} ${asyncResult.error.message}`);
        } else {
          const data = asyncResult.value.data;

          // Handle various types Office.js may return depending on environment
          if (data instanceof Uint8Array) {
            slices.push(data);
          } else if (data instanceof ArrayBuffer) {
            slices.push(new Uint8Array(data));
          } else if (ArrayBuffer.isView(data)) {
            slices.push(new Uint8Array(data.buffer, data.byteOffset, data.byteLength));
          } else if (Array.isArray(data)) {
            // Office.js sometimes returns a plain Array of bytes
            slices.push(new Uint8Array(data));
          } else {
            file.closeAsync();
            reject(`Unexpected slice data type: ${typeof data} (${data?.constructor?.name})`);
            return;
          }
          resolve();
        }
      });
    });
  }

  file.closeAsync();

  return slices;
}
